<template>
  <q-page class="row justify-center items-center column add-photo">
    <ProgressModal
      :progress="progress"
      :isProgress="isProgress"
      :status="uploadStatus"
      :speedBps="uploadSpeedBps"
      :confirmedCount="confirmedPhotosCount"
      :totalCount="imageCount"
      :activeRequests="activeUploadRequests"
      :pendingCount="pendingPhotosCount"
      :deferredCount="deferredPhotosCount"
      :failedCount="failedPhotosCount"
      :uploadedBytes="displayUploadedBytes"
      :totalBytes="uploadTotalBytes"
      :isPaused="uploadPaused"
      :canPause="isUploadRunning && !uploadAbortRequested"
      @pause="pauseUpload"
      @resume="resumeUpload"
      @sendLater="sendLaterUpload"
    />

    <UploadGuideDialog
      v-model="uploadGuideVisible"
      @hide="markUploadGuideSeen"
    />

    <BackgroundCircles />

    <div class="q-pa-md add-photo__form">
      <LoginOverlay v-if="!user" />

      <q-stepper
        v-model="step"
        ref="stepper"
        color="primary"
        animated
        keep-alive
      >
        <q-step
          class="q-step-uploader"
          :name="1"
          :title="$t('addMedia.addPhotoTitle')"
          icon="image"
          :done="step > 1"
          style="min-height: 200px"
        >
          <UploadRecoveryBanner
            v-if="pendingResumeSummary"
            :summary="pendingResumeSummary"
            :awaitingReselection="resumeAwaitingReselection"
            :canResumeSelected="canResumeSelectedFiles"
            @resume="resumePendingUpload"
            @resume-selected="resumeFromSelectedFiles"
            @discard="discardPendingUpload"
          />

          <div class="q-mb-md q-mt-md">
            <q-toggle
              v-model="filePickerThumbs"
              :label="$t('common.turnOnThumbs')"
            />
            <q-tooltip anchor="top middle" self="top middle">
              ({{ $t('common.turnOnThumbsInfo') }})
            </q-tooltip>
          </div>
          <div style="position: relative">
            <q-icon name="photo" class="step-content__icon" />
            <FilePicker
              v-if="!reload"
              :label="$t('addMedia.addFiles')"
              ref="FilePickerRef"
              fileType="image"
              :filePickerThumbs="filePickerThumbs"
              @filesSelected="setImages"
            />
            <q-btn
              v-if="supportsFileSystemAccess"
              outline
              color="primary"
              icon="folder_open"
              class="q-mt-sm full-width"
              :disable="isUploadRunning"
              :label="$t('addMedia.resume.chooseFolder')"
              @click="selectResumableFolder"
            />
            <div class="text-center q-mt-sm">
              <q-btn
                flat
                dense
                no-caps
                color="primary"
                icon="info_outline"
                :label="$t('addMedia.guide.open')"
                @click="openUploadGuide"
              />
            </div>
          </div>
        </q-step>

        <q-step
          :name="2"
          :title="$t('common.completeData')"
          icon="settings"
          :done="step > 2"
          style="min-height: 200px"
        >
          <InformationForm
            @setCity="(val) => (selectedCity = val)"
            @setCountry="(val) => (selectedCountry = val)"
            @setEvent="(val) => (selectedEvent = val)"
          />
        </q-step>

        <q-step
          :name="3"
          :title="$t('common.summary')"
          icon="add_comment"
          style="min-height: 200px"
        >
          <StepperSummary
            :selectedCity="selectedCity"
            :selectedCountry="selectedCountry"
            :selectedEvent="selectedEvent"
            :mediaCount="selectedImage.length"
            :mediaTypeLabel="$t('common.imagesCount')"
          />
        </q-step>

        <template v-slot:navigation>
          <q-stepper-navigation>
            <q-btn
              @click="handleClick"
              :disable="isNextDisabled"
              color="primary"
              v-ripple
              :label="primaryActionLabel"
            />
            <q-btn
              v-if="step > 1"
              flat
              v-ripple
              color="primary"
              @click="$refs.stepper.previous()"
              :label="$t('common.return')"
              class="q-ml-sm"
            />
            <p class="add-photo-agreement">
              {{ $t('addMedia.agreement') }} -
              <a :href="statuteUrl">
                {{ $t('common.checkHere') }}
              </a>
            </p>
          </q-stepper-navigation>
        </template>
      </q-stepper>
    </div>
  </q-page>
</template>

<script>
import FilePicker from './components/FilePicker.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import InformationForm from './components/InformationForm.vue';
import ProgressModal from './components/ProgressModal.vue';
import BackgroundCircles from './components/BackgroundCircles.vue';
import LoginOverlay from './components/LoginOverlay.vue';
import StepperSummary from './components/StepperSummary.vue';
import UploadGuideDialog from './components/upload/UploadGuideDialog.vue';
import UploadRecoveryBanner from './components/upload/UploadRecoveryBanner.vue';
import photoUploadGuidanceMixin from '@/mixins/photo-upload/photo-upload-guidance.mixin.js';
import photoUploadResumeMixin from '@/mixins/photo-upload/photo-upload-resume.mixin.js';
import photoUploadTelemetryMixin from '@/mixins/photo-upload/photo-upload-telemetry.mixin.js';
import photoUploadManagerMixin from '@/mixins/photo-upload/photo-upload-manager.mixin.js';

import 'natural-compare-lite';

export default {
  name: 'AddPhotoPage',
  mixins: [
    photoUploadManagerMixin,
    photoUploadResumeMixin,
    photoUploadTelemetryMixin,
    photoUploadGuidanceMixin,
  ],
  components: {
    FilePicker,
    ProgressModal,
    InformationForm,
    BackgroundCircles,
    LoginOverlay,
    StepperSummary,
    UploadGuideDialog,
    UploadRecoveryBanner,
  },
  data() {
    return {
      step: 1,
      selectedCountry: '',
      selectedCity: '',
      selectedEvent: '',
      filePickerThumbs: false,
      selectedImage: [],
      reload: false,
      success: true,
      statuteUrl: process.env.VUE_APP_BACK_ROOT + this.$t('common.statuteRoute'),
    };
  },
  computed: {
    ...mapGetters('global', ['getFormData']),
    ...mapState({
      user: (state) => state.auth.user,
      citiesList: (state) => state.global.citiesList,
      countriesList: (state) => state.global.countriesList,
      selectEventsList: (state) => state.global.selectEventsList,
    }),
    canResumeSelectedFiles() {
      return Boolean(
        this.pendingResumeSummary &&
        this.selectedImage.length &&
        this.selectedImage.length === this.pendingResumeSummary.remainingFiles
      );
    },
    primaryActionLabel() {
      if (this.step === 1 && this.pendingResumeSummary) {
        if (this.canResumeSelectedFiles) {
          return this.$t('addMedia.resume.resumeSelectedPrimary', {
            count: this.pendingResumeSummary.remainingFiles,
          });
        }

        if (!this.resumeAwaitingReselection) {
          return this.$t('addMedia.resume.resume');
        }

        return this.$t('addMedia.resume.selectRemainingPrimary', {
          count: this.pendingResumeSummary.remainingFiles,
        });
      }

      return this.step === 3 ? this.$t('common.submit') : this.$t('common.next');
    },
    isNextDisabled() {
      if (this.isUploadRunning || this.isUploadStarting) {
        return true;
      }

      if (this.step === 1 && this.pendingResumeSummary) {
        return this.resumeAwaitingReselection && !this.canResumeSelectedFiles;
      }

      if (this.pendingResumeSummary) {
        return true;
      }

      if (this.step === 1) {
        return !this.selectedImage.length;
      }

      if (this.step > 2) {
        return !this.selectedImage.length || !this.selectedEvent;
      }

      return false;
    },
  },
  methods: {
    ...mapActions('global', [
      'loadFormData',
      'saveFormData',
      'getCountries',
      'getCities',
      'fetchEventsForSelect'
    ]),
    setImages(data) {
      const files = [...data];
      const manifest = this._pendingResumeManifest || this._uploadManifest;

      // When recovering a saved session, users may select the original full
      // folder instead of manually picking only the remaining photos. Normalize
      // the selection to the files that are still pending in the manifest.
      if (manifest && this.pendingResumeSummary && this.resumeAwaitingReselection) {
        const { matched } = this.matchResumeFiles(manifest, files);
        this.selectedImage = matched;
      } else {
        this.selectedImage = files;
      }

      this.maybeShowLargeUploadNotice(this.selectedImage.length);
    },
    goToLogin() {
      this.$router.push('/login');
    },
    async handleClick() {
      if (this.step === 1 && this.pendingResumeSummary) {
        if (this.canResumeSelectedFiles) {
          await this.resumeFromSelectedFiles();
        } else if (!this.resumeAwaitingReselection) {
          await this.resumePendingUpload();
        }
        return;
      }

      if (this.step === 3) {
        if (!this.user) return;
        if (this.selectedEvent && this.selectedImage.length) {
          const formData = {
            city: this.selectedEvent.assign_city,
            country: this.selectedCountry.id,
            event: this.selectedEvent.id,
          };
          this.saveFormData(formData);

          const files = [...this.selectedImage];
          this.startPhotoUpload(files);
        } else {
          if (!this.selectedImage.length) {
            this.step = 1;
          }
          if (!this.selectedEvent) {
            this.step = 2;
          }

          this.$q.notify({
            icon: 'warning',
            color: 'negative',
            message: this.$t('validation.completeAllFields'),
            position: 'top-right',
          });
        }
      } else {
        this.$refs.stepper.next();
      }
    },
    showPrefillFormMessage() {
      this.$q.notify({
        message: this.$t('dashboard.menu.prefillForm'),
        color: 'positive',
        icon: 'info',
        position: 'top-right',
        timeout: 3000,
      });
    },
  },
  watch: {
    user(newUser) {
      if (newUser?.id) {
        this.detectPendingUploadManifest()
          .finally(() => this.showUploadGuideIfNeeded());
      }
    },
  },
  mounted() {
    if (this.user && (!Array.isArray(this.selectEventsList) || this.selectEventsList.length < 1)) {
      this.fetchEventsForSelect();
    }

    if (!Array.isArray(this.citiesList) || this.citiesList.length < 1) {
      this.getCities();
    }

    if (!Array.isArray(this.countriesList) || this.countriesList.length < 1) {
      this.getCountries();
    }

    this.loadFormData();
    if (this.getFormData !== null) {
      this.showPrefillFormMessage();
    }
    if (this.user?.id) {
      this.detectPendingUploadManifest()
        .finally(() => this.showUploadGuideIfNeeded());
    }
  },
};
</script>

<style lang="scss" scoped>
.add-photo {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding-top: 150px;
  &__button {
    width: 160px;
  }

  &__form {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: inherit;
    z-index: 3;
    position: relative;
  }
}
::v-deep {
  .q-uploader__header-content {
    > div {
      a {
        &:nth-of-type(3) {
          display: none;
        }
      }
    }
  }

  .q-stepper {
    -webkit-box-shadow: 0px 0px 10px 0px #ffffff;
    box-shadow: 0px 0px 10px 0px #ffffff;
    @media (max-width: 548px) {
      min-width: 90%;
      &__label {
        display: none;
      }
      .q-stepper__dot:before {
        display: none;
      }
    }
    @media (max-width: 380px) {
      min-width: 100%;
      width: 100%;
    }

    .add-photo-agreement {
      margin-top: 1rem;
    }
  }
  .q-step-uploader {
    .q-stepper {
      &__step {
        &-inner {
          padding-top: 0;
          padding-left: 0;
          padding-right: 0;
          justify-content: center;
          flex-direction: column;
          align-items: center;
          position: relative;
          .step-content__icon {
            display: block;
            font-size: 2.5rem;
            color: #fb205655;
            position: absolute;
            top: 1rem;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1;
            @media (max-width: 400px) {
              display: none;
            }
          }
          .q-uploader {
            max-width: 100%;
            box-shadow: none;
            @media (min-width: 548px) {
              &__list {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                padding: 24px;
              }
              &__file {
                margin: 6px;
                &--img {
                  height: 100px;
                  width: 100px;
                }
              }
              &__header {
                &-content {
                  padding: 32px;
                }
              }
            }
            &__header {
              &-content {
                padding: 18px;
              }
            }
          }
        }
      }
    }
  }
}

@media (max-width: 450px) {
  .add-photo {
    &__title {
      font-size: 22px;
      padding: 8px;
      margin: 8px 12px;
      top: 10px;
      width: 90%;
    }
  }
}
</style>
